import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutGrid, FileText, Briefcase, Target, Users, Globe2, Layers, RefreshCw, Wallet, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Img } from "@/components/site/ServiceBlocks";
import { OFFICES } from "@/components/site/SiteLayout";

const OFFERS = [
  { icon: LayoutGrid, color: "#1074BA", title: "Dashboards", to: "/dashboards",
    company: ["Track demand, prices, capacity, cost of production and CAPEX in one place", "Spot margin squeezes and new opportunities early", "Excel-ready data for budgeting and S&OP"],
    industry: "A shared, regularly updated view of supply, demand and cost across regions." },
  { icon: FileText, color: "#03AB9E", title: "Special Reports", to: "/reports",
    company: ["Product-level market trends, forecasts and opportunity analysis", "Fast input for business plans, investments and due diligence", "Coverage of commodities and niche specialty chemicals"],
    industry: "Transparent market sizing that helps buyers, producers and investors plan capacity." },
  { icon: Briefcase, color: "#F58B23", title: "Consulting", to: "/consulting",
    company: ["Go-to-market, capacity risk and value-chain profitability studies", "Technology transfer and plant transaction advisory", "Answers tailored to your specific decision"],
    industry: "Better-informed investments that reduce over-capacity and stranded assets." },
];

const WHY = [
  [Target, "Outcome-focused", "Every engagement starts with the decision you need to make and ends with a clear recommendation."],
  [Users, "Boutique & senior-led", "A small team of chemical-industry specialists who work directly on your project."],
  [Layers, "Deep product coverage", "C2, C3, C4 chains, aromatics and 35+ specialty chemical categories, down to sub-segment level."],
  [RefreshCw, "Always up to date", "Monthly and quarterly trackers, reviewed by analysts and industry experts."],
  [Globe2, "Global view, local insight", "Analysts across regions, with regional prices, costs and capacity."],
  [Wallet, "Budget friendly", "Subscribe only to the products and regions you need."],
];

const JOURNEY = [
  ["2019", "Founded", "CCMIntel is established as a boutique chemical market intelligence and consulting firm."],
  ["Consulting", "Client engagements", "Market entry, feasibility, cost benchmarking and due diligence for producers and investors."],
  ["Reports", "Special reports library", "A growing library of product-level market trends, forecast and opportunity reports."],
  ["Platform", "Dashboard-as-a-Service", "Demand, price, capacity, cost of production and CAPEX dashboards fed by our databases."],
  ["Today", "One intelligence platform", "Dashboards, reports, consulting, tech transfer and plant advisory under one roof."],
];

function Stats() {
  const { data: cats = [] } = useQuery({ queryKey: ["report-cats"], queryFn: async () => (await api.get("/reports/categories")).data });
  const reports = cats.reduce((a, c) => a + (c.count || 0), 0);
  const stats = [["2019", "Established"], [reports ? `${Math.floor(reports / 10) * 10}+` : "740+", "Special reports"], [cats.length ? `${cats.length}` : "57", "Chemical categories"],
    ["110+", "Dashboard products"], [`${OFFICES.length}`, "Offices in India"], ["Global", "Analyst network"]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="about-stats">
      {stats.map(([v, l]) => (
        <div key={l} className="bg-[#0F172A]/80 border border-slate-800 rounded-lg p-5 fade-up">
          <p className="text-3xl font-extrabold font-heading text-[#F58B23]">{v}</p>
          <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider font-mono">{l}</p>
        </div>
      ))}
    </div>
  );
}

export function AboutContent() {
  return (
    <div className="space-y-20" data-testid="about-content">
      <section className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F58B23]">Our story</p>
          <h2 className="text-3xl md:text-4xl font-bold mt-3">A boutique firm built around your outcomes</h2>
          <div className="space-y-4 mt-5 text-slate-300 text-sm md:text-base leading-relaxed">
            <p>Cognitive Market Intelligence Pvt. Ltd. (CCMIntel) was established in 2019 to give chemical companies the market clarity that is usually locked inside large consultancies, in a form that is faster, more focused and easier to use.</p>
            <p>We are a boutique consulting and intelligence firm. We stay small and senior on purpose, so every client works directly with specialists who know petrochemicals, downstream derivatives and specialty chemicals in depth.</p>
            <p>Our measure of success is simple: did the client make a better decision? Whether it is entering a new market, investing in capacity, buying a plant or pricing a product, we focus on outcomes rather than page counts.</p>
          </div>
        </div>
        <div className="on-photo relative rounded-lg overflow-hidden aspect-[4/3]">
          <Img name="scientist" alt="Chemical industry research" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090D16] via-[#090D16]/40 to-transparent" />
          <p className="absolute bottom-5 left-5 right-5 text-white font-heading text-xl font-semibold">Data, analysis and industry experience, combined to help clients succeed.</p>
        </div>
      </section>

      <Stats />

      <section>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#03AB9E]">What we do</p>
        <h2 className="text-3xl md:text-4xl font-bold mt-3">How we help chemical companies and the industry</h2>
        <div className="grid lg:grid-cols-3 gap-4 mt-8">
          {OFFERS.map((o) => (
            <div key={o.title} className="bg-[#0F172A]/80 border border-slate-800 rounded-lg p-6 flex flex-col hover:-translate-y-1 transition-transform duration-300" style={{ borderTop: `3px solid ${o.color}` }} data-testid={`about-offer-${o.title.toLowerCase().replace(/\s/g, "-")}`}>
              <o.icon className="h-6 w-6" style={{ color: o.color }} />
              <h3 className="text-xl font-bold mt-3">{o.title}</h3>
              <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mt-4 mb-2">For your company</p>
              <ul className="space-y-1.5">{o.company.map((c) => <li key={c} className="flex gap-2 text-sm text-slate-300"><CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" style={{ color: o.color }} />{c}</li>)}</ul>
              <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mt-5 mb-2">For the industry</p>
              <p className="text-sm text-slate-400 flex-1">{o.industry}</p>
              <Link to={o.to} className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: o.color }}>Explore {o.title.toLowerCase()} <ArrowRight className="h-4 w-4" /></Link>
            </div>
          ))}
        </div>
      </section>

      <section>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F58B23]">Why CCMIntel</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 mt-6">
          {WHY.map(([Icon, t, d]) => (
            <div key={t} className="flex gap-4"><span className="h-10 w-10 shrink-0 rounded-lg bg-[#1074BA]/15 grid place-items-center"><Icon className="h-5 w-5 text-[#1074BA]" /></span>
              <div><p className="font-semibold">{t}</p><p className="text-sm text-slate-400 mt-1">{d}</p></div></div>
          ))}
        </div>
      </section>

      <section data-testid="about-journey">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#03AB9E]">Our journey</p>
        <ol className="relative mt-8 grid md:grid-cols-5 gap-6">
          <div className="hidden md:block absolute top-[11px] left-0 right-0 h-px bg-gradient-to-r from-[#1074BA] via-[#03AB9E] to-[#F58B23]" />
          {JOURNEY.map(([tag, t, d], i) => (
            <li key={t} className="relative">
              <span className="relative z-10 block h-6 w-6 rounded-full border-4 border-[#090D16]" style={{ background: ["#1074BA", "#0E86C9", "#03AB9E", "#F5A623", "#F58B23"][i] }} />
              <p className="font-mono text-xs text-[#F58B23] mt-3">{tag}</p>
              <p className="font-semibold mt-1">{t}</p>
              <p className="text-sm text-slate-400 mt-1">{d}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="rounded-lg border border-[#1074BA]/40 bg-[#1074BA]/10 p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex-1"><h2 className="text-2xl md:text-3xl font-bold">Have a decision to make?</h2><p className="text-slate-300 mt-2 text-sm md:text-base">Tell us about your product, market or project, and we'll suggest the quickest way to an answer.</p></div>
        <Link to="/contact" data-testid="about-contact-button" className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-[#F58B23] hover:bg-[#D97618] text-slate-950 font-semibold text-sm">Contact us <ArrowRight className="h-4 w-4" /></Link>
      </section>
    </div>
  );
}
